import { EmojiPickerDropdown } from "@/components/emoji/EmojiPickerDropdown.tsx";
import { parseVideoUrl } from "@/components/rich-editor/extensions/video-url.ts";
import { insertEmoji } from "@/components/rich-editor/rich-editor.helpers.ts";
import {
  PlaceholderOption,
  RichEditorBodyFileType,
} from "@/components/rich-editor/rich-editor.types.ts";
import { VariableInsertMenu } from "@/components/variable-insert-menu/VariableInsertMenu.tsx";
import { TemplateEmailChooseModal } from "@/pages/settings/recruit/templates/widgets/TemplateEmailChooseModal.tsx";
import { ApiTemplate } from "@/types/template.types.ts";
import {
  Box,
  Button,
  ButtonIcon,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import { Editor } from "@tiptap/react";
import { forwardRef, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

const ALIGNMENTS = [
  { value: "left", icon: "fa-light fa-align-left", label: "common:editor_align_left" },
  { value: "center", icon: "fa-light fa-align-center", label: "common:editor_align_center" },
  { value: "right", icon: "fa-light fa-align-right", label: "common:editor_align_right" },
  { value: "justify", icon: "fa-light fa-align-justify", label: "common:editor_align_justify" },
] as const;

// rgb() as ProseMirror serializes it, so reopened text matches its swatch; ≥3:1 on white and dark.
const TEXT_COLORS = [
  { value: "rgb(125, 125, 125)", label: "common:editor_color_gray" },
  { value: "rgb(229, 68, 31)", label: "common:editor_color_red" },
  { value: "rgb(203, 94, 16)", label: "common:editor_color_orange" },
  { value: "rgb(14, 144, 66)", label: "common:editor_color_green" },
  { value: "rgb(0, 129, 214)", label: "common:editor_color_blue" },
  { value: "rgb(179, 78, 223)", label: "common:editor_color_violet" },
] as const;

// Translucent so both dark-theme light text and light-theme dark text stay readable on the fill.
const BACKGROUND_COLORS = [
  { value: "rgba(245, 166, 35, 0.3)", label: "common:editor_color_yellow" },
  { value: "rgba(125, 125, 125, 0.3)", label: "common:editor_color_gray" },
  { value: "rgba(229, 68, 31, 0.3)", label: "common:editor_color_red" },
  { value: "rgba(203, 94, 16, 0.3)", label: "common:editor_color_orange" },
  { value: "rgba(14, 144, 66, 0.3)", label: "common:editor_color_green" },
  { value: "rgba(0, 129, 214, 0.3)", label: "common:editor_color_blue" },
  { value: "rgba(179, 78, 223, 0.3)", label: "common:editor_color_violet" },
] as const;

const ColorSwatch = ({ color }: { color: string }) => (
  <span
    className="size-4 shrink-0 rounded-full border border-dark/10"
    style={{ background: color }}
  />
);

type ToolbarProps = React.HTMLAttributes<HTMLDivElement> & {
  editor: Editor;
  emailTemplates?: ApiTemplate[];
  onApplyTemplate?: (template: ApiTemplate) => void;
  placeholderOptions?: PlaceholderOption[];
  insertAndUploadImage?: (file: File) => Promise<void>;
  insertAndUploadBodyFile?: (file: File, type: RichEditorBodyFileType) => Promise<void>;
  toolbarClassName?: string;
  insertEmojiIsOn?: boolean;
  hideHeadings?: boolean;
  hideLinks?: boolean;
};

export const RichEditorToolbar = forwardRef<HTMLDivElement, ToolbarProps>(
  (
    {
      editor,
      emailTemplates,
      onApplyTemplate,
      placeholderOptions,
      insertAndUploadImage,
      insertAndUploadBodyFile,
      toolbarClassName,
      insertEmojiIsOn,
      hideHeadings,
      hideLinks,
      ...divProps
    },
    ref
  ) => {
    const { openModal } = useModals();
    const { showError } = useToasts();

    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const videoInputRef = useRef<HTMLInputElement>(null);
    const pdfInputRef = useRef<HTMLInputElement>(null);
    const [uploadingBodyFile, setUploadingBodyFile] = useState<RichEditorBodyFileType | null>(null);

    const setLink = useCallback(() => {
      const previousUrl = editor.getAttributes("link").href;
      const url = window.prompt("URL", previousUrl);

      if (url === null) return;
      if (url === "") {
        editor.chain().focus().extendMarkRange("link").unsetLink().run();
        return;
      }
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }, [editor]);

    const insertPlaceholder = useCallback(
      (placeholder: string) => {
        editor
          .chain()
          .focus()
          .insertContent(placeholder + " ")
          .run();
      },
      [editor]
    );

    const handleFileInputChange = useCallback(
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && insertAndUploadImage) {
          await insertAndUploadImage(file);
        }
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      },
      [insertAndUploadImage]
    );

    const insertVideoLink = useCallback(() => {
      const url = window.prompt(t("common:editor_video_link_prompt"));
      if (!url?.trim()) return;
      if (!parseVideoUrl(url)) {
        showError({ title: t("common:editor_video_link_unsupported") });
        return;
      }
      editor
        .chain()
        .focus()
        .insertContent({ type: "video", attrs: { src: url.trim() } })
        .run();
    }, [editor, showError, t]);

    const handleBodyFileInputChange = useCallback(
      async (e: React.ChangeEvent<HTMLInputElement>, type: RichEditorBodyFileType) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file || !insertAndUploadBodyFile) return;
        setUploadingBodyFile(type);
        try {
          await insertAndUploadBodyFile(file, type);
        } finally {
          setUploadingBodyFile(null);
        }
      },
      [insertAndUploadBodyFile]
    );

    const triggerFileInput = useCallback(() => {
      fileInputRef.current?.click();
    }, []);

    const isAlignmentActive = (alignment: (typeof ALIGNMENTS)[number]["value"]) =>
      alignment === "left"
        ? !ALIGNMENTS.some(({ value }) => value !== "left" && editor.isActive({ textAlign: value }))
        : editor.isActive({ textAlign: alignment });

    const handleInsertTemplate = () => {
      openModal(TemplateEmailChooseModal, {
        onApply: (template) => {
          editor.commands.setContent(template.body);
          onApplyTemplate?.(template);
        },
      });
    };

    return (
      <Box
        ref={ref}
        {...divProps}
        className={twMerge(
          `border border-dark/5 px-3 py-3 rounded-t-xs -ml-[1px] -mr-[1px] -mt-[1px]`,
          toolbarClassName
        )}
      >
        <Stack direction={"horizontal"} gap={"lg"} className={"flex-wrap"}>
          {!hideHeadings && (
            <Stack direction={"horizontal"} gap={"xs"}>
              <ButtonIcon
                variant={editor.isActive("heading", { level: 2 }) ? "secondary" : "plain"}
                icon={<FontAwesomeIcon icon={"fa-light fa-h2"} />}
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              />
              <ButtonIcon
                variant={editor.isActive("heading", { level: 3 }) ? "secondary" : "plain"}
                icon={<FontAwesomeIcon icon={"fa-light fa-h3"} />}
                onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              />
              <ButtonIcon
                variant={editor.isActive("heading", { level: 4 }) ? "secondary" : "plain"}
                icon={<FontAwesomeIcon icon={"fa-light fa-h4"} />}
                onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
              />
            </Stack>
          )}
          <Stack direction={"horizontal"} gap={"xs"}>
            <ButtonIcon
              variant={editor.isActive("bold") ? "secondary" : "plain"}
              icon={<FontAwesomeIcon icon={"fa-light fa-bold"} />}
              onClick={() => editor.chain().focus().toggleBold().run()}
            />
            <ButtonIcon
              variant={editor.isActive("italic") ? "secondary" : "plain"}
              icon={<FontAwesomeIcon icon={"fa-light fa-italic"} />}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            />
            <ButtonIcon
              variant={editor.isActive("underline") ? "secondary" : "plain"}
              icon={<FontAwesomeIcon icon={"fa-light fa-underline"} />}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
            />
            <DropdownMenu
              trigger={
                <ButtonIcon
                  variant={editor.getAttributes("textStyle").color ? "secondary" : "plain"}
                  title={t("common:editor_text_color")}
                  icon={<FontAwesomeIcon icon={"fa-light fa-font"} />}
                />
              }
            >
              <DropdownMenuItem
                title={t("common:editor_color_default")}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-ban"} />}
                isSelected={!editor.getAttributes("textStyle").color}
                onClick={() => editor.chain().focus().unsetColor().run()}
              />
              {TEXT_COLORS.map(({ value, label }) => (
                <DropdownMenuItem
                  key={value}
                  title={t(label)}
                  leftIcon={<ColorSwatch color={value} />}
                  isSelected={editor.isActive("textStyle", { color: value })}
                  onClick={() => editor.chain().focus().setColor(value).run()}
                />
              ))}
            </DropdownMenu>
            <DropdownMenu
              trigger={
                <ButtonIcon
                  variant={
                    editor.getAttributes("textStyle").backgroundColor ? "secondary" : "plain"
                  }
                  title={t("common:editor_background_color")}
                  icon={<FontAwesomeIcon icon={"fa-light fa-fill-drip"} />}
                />
              }
            >
              <DropdownMenuItem
                title={t("common:editor_no_background")}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-ban"} />}
                isSelected={!editor.getAttributes("textStyle").backgroundColor}
                onClick={() => editor.chain().focus().unsetBackgroundColor().run()}
              />
              {BACKGROUND_COLORS.map(({ value, label }) => (
                <DropdownMenuItem
                  key={value}
                  title={t(label)}
                  leftIcon={<ColorSwatch color={value} />}
                  isSelected={editor.isActive("textStyle", { backgroundColor: value })}
                  onClick={() => editor.chain().focus().setBackgroundColor(value).run()}
                />
              ))}
            </DropdownMenu>
          </Stack>
          <Stack direction={"horizontal"} gap={"xs"}>
            {ALIGNMENTS.map(({ value, icon, label }) => (
              <ButtonIcon
                key={value}
                variant={isAlignmentActive(value) ? "secondary" : "plain"}
                title={t(label)}
                icon={<FontAwesomeIcon icon={icon} />}
                onClick={() =>
                  value === "left"
                    ? editor.chain().focus().unsetTextAlign().run()
                    : editor.chain().focus().setTextAlign(value).run()
                }
              />
            ))}
          </Stack>
          <Stack direction={"horizontal"} gap={"xs"}>
            <ButtonIcon
              variant={editor.isActive("bulletList") ? "secondary" : "plain"}
              icon={<FontAwesomeIcon icon={"fa-light fa-list"} />}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            />
            <ButtonIcon
              variant={editor.isActive("orderedList") ? "secondary" : "plain"}
              icon={<FontAwesomeIcon icon={"fa-light fa-list-ol"} />}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            />
            <ButtonIcon
              variant="plain"
              title={t("common:editor_horizontal_rule")}
              icon={<FontAwesomeIcon icon={"fa-light fa-horizontal-rule"} />}
              onClick={() => editor.chain().focus().setHorizontalRule().run()}
            />
            <ButtonIcon
              variant="plain"
              title={t("common:editor_clear_formatting")}
              icon={<FontAwesomeIcon icon={"fa-light fa-text-slash"} />}
              onClick={() => editor.chain().focus().unsetAllMarks().unsetTextAlign().run()}
            />
          </Stack>
          <Stack direction={"horizontal"} gap={"xs"}>
            {!hideLinks && (
              <ButtonIcon
                variant={editor.isActive("link") ? "secondary" : "plain"}
                icon={<FontAwesomeIcon icon={"fa-light fa-link"} />}
                onClick={() => setLink()}
              />
            )}
            {insertAndUploadImage && (
              <Stack direction={"horizontal"} gap={"xs"}>
                <ButtonIcon
                  variant="plain"
                  icon={<FontAwesomeIcon icon={"fa-light fa-image"} />}
                  onClick={triggerFileInput}
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileInputChange}
                  accept="image/*"
                  style={{ display: "none" }}
                />
              </Stack>
            )}
            {insertAndUploadBodyFile && (
              <>
                <DropdownMenu
                  trigger={
                    <ButtonIcon
                      variant="plain"
                      title={t("common:editor_video")}
                      disabled={!!uploadingBodyFile}
                      icon={
                        <FontAwesomeIcon
                          icon={
                            uploadingBodyFile === "video"
                              ? "fa-light fa-spinner fa-spin"
                              : "fa-light fa-film"
                          }
                        />
                      }
                    />
                  }
                >
                  <DropdownMenuItem
                    title={t("common:editor_video_link")}
                    leftIcon={<FontAwesomeIcon icon={"fa-light fa-link"} />}
                    onClick={insertVideoLink}
                  />
                  <DropdownMenuItem
                    title={t("common:editor_video_file")}
                    leftIcon={<FontAwesomeIcon icon={"fa-light fa-upload"} />}
                    onClick={() => videoInputRef.current?.click()}
                  />
                </DropdownMenu>
                <input
                  type="file"
                  ref={videoInputRef}
                  onChange={(e) => handleBodyFileInputChange(e, "video")}
                  accept="video/mp4,video/webm"
                  style={{ display: "none" }}
                />
                <ButtonIcon
                  variant="plain"
                  title={t("common:editor_pdf")}
                  disabled={!!uploadingBodyFile}
                  icon={
                    <FontAwesomeIcon
                      icon={
                        uploadingBodyFile === "pdf"
                          ? "fa-light fa-spinner fa-spin"
                          : "fa-light fa-file-pdf"
                      }
                    />
                  }
                  onClick={() => pdfInputRef.current?.click()}
                />
                <input
                  type="file"
                  ref={pdfInputRef}
                  onChange={(e) => handleBodyFileInputChange(e, "pdf")}
                  accept="application/pdf"
                  style={{ display: "none" }}
                />
              </>
            )}
            {insertEmojiIsOn && (
              <EmojiPickerDropdown onEmojiSelect={(emoji) => insertEmoji(editor, emoji)}>
                <Button
                  variant={"plain"}
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-face-smile-plus" />}
                ></Button>
              </EmojiPickerDropdown>
            )}
          </Stack>

          {placeholderOptions && (
            <VariableInsertMenu options={placeholderOptions} onInsert={insertPlaceholder} />
          )}

          {emailTemplates && emailTemplates.length > 0 && (
            <Button
              className={"font-normal"}
              variant="plain"
              leftIcon={<FontAwesomeIcon icon="fa-light fa-pen-field" />}
              onClick={handleInsertTemplate}
            >
              {t("common:templates")}
            </Button>
          )}
        </Stack>
      </Box>
    );
  }
);
